/**
 * EAV.RDP.ANALYTICS, Type VolatilitySurface: the surfaces service's answer laid out as LSEG's
 * Excel add-in (Refinitiv for Office 26.30.2) lays it out. No display options, no transpose, no
 * empty-row removal: the rows go straight to clientFormat.
 *
 *   BaseVSFResponseProcessor (1645)     eti, fx, ir, cap, hw1f (VSFResProcessor 20350, FxVSFResponseProcessor
 *                                       87827): per answer item, its capitalised headers then its surface rows,
 *                                       or with no headers the surface transposed; an item error as three rows
 *   SwaptionVSFResProcessor (89482)     swaption: the axis names, the x values, then one row per z and y
 */

import { BaseResponseProcessor, ClientResponse, capitalize } from "../adfin/responseBase";
import { SurfacesRequest } from "./request";

type Item = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
type Rows = { rows: unknown[][]; maxRowLength: number };

// LSEG: BaseVSFResponseProcessor (1645), SwaptionVSFResProcessor (89482)
export class SurfacesResProcessor extends BaseResponseProcessor {
  /** The request item whose surfaceParameters name the swaption's axes. */
  private universe: Item | undefined;

  constructor(private readonly req: SurfacesRequest) {
    super();
  }

  // LSEG: BaseVSFResponseProcessor_1645.response
  response(e: Item): ClientResponse {
    if (e?.error !== undefined) return this.getVSFErrorResponse(e);
    if (e?.data && (e.data as Item[])[0]?.error !== undefined) return this.getVSFErrorResponse((e.data as Item[])[0]);
    const rows = this.req.surfaces === "swaption" ? this.swaptionData(e) : this.getDataFromResponse(e);
    return rows ? this.clientFormat(rows) : this.getErrorResponse({ error: { message: "Bad response syntax!", status: "", errors: [] } });
  }

  // LSEG: BaseVSFResponseProcessor_1645.getVSFErrorResponse
  getVSFErrorResponse(e: Item): ClientResponse {
    return this.getErrorResponse({ error: { errors: [], status: "", ...e.error } });
  }

  // LSEG: BaseVSFResponseProcessor_1645.addRow (no row: an empty one, not counted in the width)
  addRow(r: Rows, row?: unknown[]): void {
    if (!row) {
      r.rows.push([]);
      return;
    }
    r.rows.push(row);
    if (r.maxRowLength < row.length) r.maxRowLength = row.length;
  }

  /** The surface transposed: its column c becomes row c (rows longer than the first fail, as LSEG's). */
  // LSEG: BaseVSFResponseProcessor_1645.addColumns
  addColumns(r: Rows, surface: unknown[][] | undefined): void {
    if (!(surface && surface.length && surface[0].length)) return;
    const cols: unknown[][] = Array(surface[0].length);
    surface.forEach((row, i) =>
      row.forEach((v, c) => {
        if (i === 0) cols[c] = [];
        cols[c].push(v);
      }),
    );
    cols.forEach((row) => this.addRow(r, row));
  }

  // LSEG: BaseVSFResponseProcessor_1645.makeRowsEqualLength
  makeRowsEqualLength(r: Rows): unknown[][] {
    return r.rows.map((row) => (row.length < r.maxRowLength ? [...row, ...Array(r.maxRowLength - row.length).fill("")] : row));
  }

  /** Per answer item (a blank row between items): its error, its headers and rows, or its surface transposed. */
  // LSEG: BaseVSFResponseProcessor_1645.getDataFromResponse
  getDataFromResponse(e: Item): unknown[][] | null {
    const r: Rows = { rows: [], maxRowLength: 0 };
    if (!e || !e.data || !e.data.length) return null;
    let after = false;
    (e.data as Item[]).forEach((item) => {
      if (after) this.addRow(r);
      after = true;
      if (item.error) {
        const err = item.error;
        this.addRow(r, ["id", err.id]);
        this.addRow(r, ["status", err.status]);
        this.addRow(r, ["message", err.message]);
        return;
      }
      if (item.headers) {
        this.addRow(r, (item.headers as string[]).map((x) => capitalize(x)));
        (item.surface as unknown[][] | undefined)?.forEach((row) => this.addRow(r, row));
      } else this.addColumns(r, item.surface);
    });
    return this.makeRowsEqualLength(r);
  }

  /**
   * Per answer item: its surfaceTag (when there are several), the axis names (the request's, else
   * zAxis/yAxis/xAxis), the x values, then per z the rows `[z, y, values…]`. LSEG puts the values
   * as one nested cell after z and y; EAV spreads them over the next cells [A] (no capture shows it).
   */
  // LSEG: SwaptionVSFResProcessor_89482.getDataFromResponse, addDataRows, iterateTroughZDimension
  swaptionData(e: Item): unknown[][] | null {
    const r: Rows = { rows: [], maxRowLength: 0 };
    if (!e?.data?.length) return null;
    (e.data as Item[]).forEach((item, a) => {
      if (a > 0) this.addRow(r);
      if (item.error) {
        const err = item.error;
        this.addRow(r, ["id", err.id]);
        this.addRow(r, ["status", err.status]);
        this.addRow(r, ["message", err.message]);
        return;
      }
      this.universe = (this.req.body.universe ?? [])[a];
      if (e.data.length > 1 && item.surfaceTag) this.addRow(r, ["surface", item.surfaceTag]);
      this.addRow(r, [this.axis("zAxis"), this.axis("yAxis"), this.axis("xAxis")]);
      const x = textOrNumbers(item.x ?? []);
      const y = textOrNumbers(item.y ?? []);
      const z = textOrNumbers(item.z ?? []);
      if (item.x) this.addNested(r, ["", ""], x);
      if (item.ndimensionalArrayShape) {
        const [xCount, yCount, zCount] = [item.ndimensionalArrayShape[0], item.ndimensionalArrayShape[1], item.ndimensionalArrayShape[2]];
        (item.ndimensionalArray as unknown[][][]).forEach((plane, s) => {
          if (!plane || s >= zCount) return;
          if (s > 0) this.addRow(r);
          const zLabel = z[s] ?? `Z ${s + 1}`;
          plane.forEach((values, k) => {
            if (values && k < yCount && values.length === xCount) this.addNested(r, [zLabel, y[k] ?? `Y ${k + 1}`], textOrNumbers(values));
          });
        });
      }
    });
    return this.makeRowsEqualLength(r);
  }

  /** LSEG's row `[a, b, values]` counts 3 cells; its values spread after a and b. */
  private addNested(r: Rows, head: unknown[], values: unknown[]): void {
    this.addRow(r, [...head, values]);
    r.rows[r.rows.length - 1] = [...head, ...values];
  }

  // LSEG: SwaptionVSFResProcessor_89482.getAxis
  private axis(name: string): unknown {
    return this.universe?.surfaceParameters?.[name] ?? name;
  }
}

/** Numeric text as a number, anything else as is. */
// LSEG: SwaptionVSFResProcessor_89482.textOrNumbers
function textOrNumbers(list: unknown[]): unknown[] {
  return list.map((v) => {
    const n = Number(v);
    const f = parseFloat(v as string);
    return isNaN(n) || isNaN(f) ? v : f;
  });
}
